import React, { Fragment, Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import {
  Banner,
  FormGroup,
  FormInput,
  FormHelpText,
  Type,
  IconCheckAlt,
} from '@spotify-internal/encore-creator-web';
import tokens from '@spotify-internal/encore-foundation';
import { PodcastUpdateUrlErrorFeedback } from '../PodcastUpdateUrlErrorFeedback';
import { IDLE, LOADING } from 'store/selectors.ts';
import { AnimatedAlert } from 'components/Shared/AnimatedAlert';
import { LoadingIndicator } from 'components/Shared/LoadingIndicator';
import { trackEvent } from '../../../../lib/Tracking';
import { PodcastModalBody } from '../../../Shared/PodcastModal/index.tsx';

const StyledSuccess = styled.div`
  margin-top: ${tokens.spacer16};
  display: flex;
  align-items: center;
`;

const CheckMark = styled(IconCheckAlt)`
  margin-right: ${tokens.spacer8};
`;

const VerificationSuccess = () => (
  <StyledSuccess>
    <CheckMark color="spotifyGreenHighlight" aria-label="IconCheckAlt" iconSize={16} />
    <Type
      as="p"
      data-qa="rss-feed-valid-indicator"
      color="spotifyGreenHighlight"
      variant={Type.body1}
      condensed
    >
      {"Looking good. Let's keep going."}
    </Type>
  </StyledSuccess>
);

export class PodcastUpdateUrl extends Component {
  static propTypes = {
    podcastRssLink: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    validateFeed: PropTypes.func.isRequired,
    errors: PropTypes.array,
    network: PropTypes.shape({
      name: PropTypes.string,
    }),
    status: PropTypes.oneOf([LOADING, IDLE]),
    existingFeedUrl: PropTypes.string.isRequired,
  };

  componentDidMount() {
    trackEvent({
      category: 'feed-update',
      action: 'feed-url',
      label: 'start',
    });
  }

  onChangeWithDebouncedValidation = e => {
    const { onChange } = this.props;
    onChange(e);
    this.debouncedValidation(e.target.value.trim());
  };

  hasErrors = () => {
    return this.props.errors && this.props.errors.length > 0;
  };

  hasValidFeed = () => {
    return this.props.errors && this.props.errors.length === 0;
  };

  debouncedValidation = debounce(this.props.validateFeed, 1500);

  render() {
    const { podcastRssLink } = this.props;

    return (
      <Fragment>
        {/* istanbul ignore next */ this.hasErrors() && (
          <AnimatedAlert variant={Banner.error} message={'To continue, correct the errors below'} />
        )}
        <PodcastModalBody data-qa="rss-verification-form">
          <Type as="h1" variant={Type.heading2}>
            {"What's the new link to your podcast's RSS feed?"}
          </Type>

          <Type as="p" variant={Type.body1}>
            {`Only add a link to an RSS feed you own the rights to, and make sure your podcast content meets our `}
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="https://www.spotify.com/us/legal/end-user-agreement/"
            >
              guidelines
            </a>
            {`.`}
          </Type>
          <FormGroup label="Link to RSS feed" labelFor="podcastRssLink">
            <FormInput
              data-testid="update-feed-url"
              id="podcastRssLink"
              value={podcastRssLink}
              onChange={this.onChangeWithDebouncedValidation}
              error={this.hasErrors()}
              disabled={this.props.status === LOADING}
            />
            <FormHelpText error={this.hasErrors()}>
              {this.props.status === LOADING && (
                <LoadingIndicator
                  style={{
                    marginTop: '16px',
                    justifyContent: 'start',
                  }}
                />
              )}
              {this.hasErrors() && <PodcastUpdateUrlErrorFeedback errors={this.props.errors} />}
              {this.hasValidFeed() && <VerificationSuccess />}
            </FormHelpText>
          </FormGroup>
        </PodcastModalBody>
      </Fragment>
    );
  }
}
